import type { JsonApiHasOneResponse, StoreDto } from '@accesso/cc-ecomm-api';
import type { Observable } from 'rxjs';
import { isPlatformServer, PlatformLocation } from '@angular/common';
import { HttpClient, HttpStatusCode } from '@angular/common/http';
import { inject, Injector, PLATFORM_ID } from '@angular/core';
import { catchError, map, of, switchMap, take } from 'rxjs';

import { isNonEmptyString, LoggerService } from '@accesso/cc-std-lib';
import { LocaleModel, MerchantModel, StoreModel } from '@accesso/cc-ui/model';
import { JsonApiService } from '@accesso/ng-json-api';

import type { IEnvironmentService } from '../environment/environment.interface';
import { MerchantStore } from '../../feature/merchant/merchant.store';
import { QUERY_PARAM_LANGUAGE, QUERY_PARAM_STORE_IDENTIFIER } from '../enums/query-params';
import { EnvironmentService } from '../environment/environment.service';
import { RedirectError } from '../errors/redirect.error';

export const dataInitializer = (): Observable<void> | void => {
	const injector = inject(Injector);
	const jsonApi = inject(JsonApiService);
	const http = inject(HttpClient);
	const environment = inject<IEnvironmentService>(EnvironmentService);
	const location = inject(PlatformLocation);
	const logger = inject(LoggerService);
	const merchantStore = inject(MerchantStore);

	environment.store = getStore(location);

	if (environment.store === 'localhost' || !environment.store) return;

	const requestedLanguage = new URLSearchParams(location.search).get(QUERY_PARAM_LANGUAGE);
	const storeLanguageQuery = isNonEmptyString(requestedLanguage) ? `&ecLanguage=${requestedLanguage}` : '';

	return environment.loaded$.pipe(
		take(1),
		switchMap(() =>
			http
				.get<JsonApiHasOneResponse<StoreDto>>(`http://${environment.api}/v1/store/${environment.store}?include=merchant${storeLanguageQuery}`, {
					headers: {
						Accept: 'application/vnd.api+json',
					},
				})
				.pipe(
					catchError((err) => {
						if (err.status !== HttpStatusCode.NotFound) {
							logger.error(`Error retreiving environment ${err.message}`);
						}
						return of(null);
					}),
					take(1)
				)
		),
		map((resp: JsonApiHasOneResponse<StoreDto> | null) => {
			if (!resp?.data)
				throw {
					status: 404,
				};

			const { language, tenant } = resp.data.attributes;
			const merchantId = resp.data.relationships.merchant.data!.id;
			jsonApi.overrideApiConfiguration({
				host: environment.api,
				protocol: 'https',
				version: 'v1',
				headers: {
					'com-accessopassport-client': tenant,
					'com-accessopassport-merchant-id': merchantId,
					'com-accessopassport-language': language,
				},
			});
			return resp;
		}),
		map((resp: JsonApiHasOneResponse<StoreDto> | null) => {
			if (resp?.data) {
				const { language } = resp.data.attributes;
				if (isPlatformServer(injector.get(PLATFORM_ID)) && isNonEmptyString(requestedLanguage) && language !== requestedLanguage)
					throw new RedirectError(location.href, removeLanguageQueryParamFromUrl(location.href));

				const store = new StoreModel(injector, environment.store);
				// @ts-expect-error - The json-api library does not have the correct types
				store.from(resp.data);
				jsonApi.addEntity(StoreModel, store);

				if (Array.isArray(resp.included)) {
					const merchant = new MerchantModel(injector, resp.included[0]!.id);

					// @ts-expect-error - The json-api library does not have the correct types
					merchant.from(resp.included[0]);

					merchantStore.initialize(merchant, store, language, jsonApi.findOne(LocaleModel, 'lang'));
					jsonApi.addEntity(MerchantModel, merchant);
				}
			}
		})
	);
};

/**
 * Returns the store domain
 * @param location The platform location service
 */
export function getStore(location: PlatformLocation): string {
	if (location.search) {
		const store = new URLSearchParams(location.search).get(QUERY_PARAM_STORE_IDENTIFIER);

		if (store) {
			return store;
		}
	}

	return location.hostname.split('.')[0]!;
}

/**
 * Removes the language query parameter from the URL
 * @param url The url to replace the query param on
 */
function removeLanguageQueryParamFromUrl(url: string): string {
	const [domain, queryString] = url.split('?');
	const searchParams = new URLSearchParams(queryString ?? '');

	searchParams.delete(QUERY_PARAM_LANGUAGE);

	return `${domain}?${searchParams.toString()}`;
}
